import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import {
  QueryDocumentSnapshot,
  QueryFieldFilterConstraint,
  Timestamp,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { database, functions } from "../..";
import Fee from "../../../interfaces/Fee";
import {
  camposFaltantesParaEntrega,
  type CampoEntrega,
} from "../../../utils/entregaValidations";
import { handleAuthError, isAuthError } from "../../../utils/handleAuthError";
import { Usuario } from "../usuarios";

dayjs.extend(isSameOrBefore);

interface Rastreo {
  estado: string;
  fecha: Timestamp;
  hora: Timestamp;
}

export interface Paquete {
  id: string;
  idRastreo: string;
  idUsuario: string;
  contenido: string;
  ubicacionFisica?: string;
  estado: string;
  peso: {
    monto: number;
    unidad: "kg" | "lb";
  };
  tarifa: Fee;
  cobroAdicional: number;
  total: number;
  via: "maritimo" | "aereo";
  rastreo: Rastreo[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  observaciones?: string;
  costo?: number;
  ganancia?: number;
  detallesEntrega?: string;
  puntosAcumulados?: number;
  puntosRedimidos?: number;
  descuento?: number;
  envio?: string;
}

export interface Analytics {
  packages: number;
  sales: number;
  cuentasPorCobrar: number;
  costos: number;
  ganancia: number;
  aereo: number;
  maritimo: number
}

export interface PaqueteDto extends Paquete {
  usuario: Usuario;
}

const guardarPaquete = httpsCallable(functions, "guardarPaquete");

export async function countPackagesByEstado(
  fromDate?: Date,
  toDate?: Date,
  estadosIds?: string[]
): Promise<Record<string, number> & { total: number }> {
  const db = getFirestore();
  const paquetesRef = collection(db, "paquetes");

  const fechaFilters: QueryFieldFilterConstraint[] = [];

  if (fromDate) fechaFilters.push(where("createdAt", ">=", fromDate));

  if (toDate) fechaFilters.push(where("createdAt", "<=", toDate));

  // Si no se proporcionan estados, obtener todos los estados únicos de los paquetes
  let estadosToCount: string[] = estadosIds || [];
  
  if (estadosToCount.length === 0) {
    // Obtener todos los estados únicos de los paquetes en el rango de fechas
    const allPaquetesQuery = query(paquetesRef, ...fechaFilters);
    const allPaquetesSnapshot = await getDocs(allPaquetesQuery);
    const estadosUnicos = new Set<string>();
    allPaquetesSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.estado) {
        estadosUnicos.add(data.estado);
      }
    });
    estadosToCount = Array.from(estadosUnicos);
  }

  const counts = await Promise.all(
    estadosToCount.map(async (estado) => {
      const queryRef = query(
        paquetesRef,
        where("estado", "==", estado),
        ...fechaFilters
      );
      const snapshot = await getCountFromServer(queryRef);
      return { estado, count: snapshot.data().count };
    })
  );

  const totalQuery = query(paquetesRef, ...fechaFilters);
  const totalSnapshot = await getCountFromServer(totalQuery);
  const total = totalSnapshot.data().count;

  const countsByEstado = counts.reduce(
    (acc, curr) => ({ ...acc, [curr.estado]: curr.count }),
    {} as Record<string, number>
  );

  return {
    ...countsByEstado,
    total,
  };
}

export async function fetchPaquetesLoteado(
  lastDoc?: QueryDocumentSnapshot,
  fromDate?: Date,
  toDate?: Date
): Promise<{
  paquetes: PaqueteDto[];
  lastDoc?: QueryDocumentSnapshot;
}> {
  const db = getFirestore();
  const paquetesRef = collection(db, "paquetes");

  const paquetesQuery = query(
    paquetesRef,
    where("createdAt", ">=", fromDate),
    where("createdAt", "<=", toDate),
    orderBy("idRastreo", "desc"),
    ...(lastDoc ? [startAfter(lastDoc)] : []),
    limit(100)
  );

  const snapshot = await getDocs(paquetesQuery);

  const paquetesRaw = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as Partial<Paquete>),
  })) as Paquete[];

  const userIds = Array.from(
    new Set(paquetesRaw.map((p) => p.idUsuario).filter(Boolean))
  );

  const userMap: Record<string, any> = {};

  await Promise.all(
    userIds.map(async (idUsuario) => {
      const userDoc = await getDoc(doc(db, "users", idUsuario));
      if (userDoc.exists()) {
        userMap[idUsuario] = { id: userDoc.id, ...userDoc.data() };
      }
    })
  );

  const paquetesConUsuario = paquetesRaw.map((p) => ({
    ...p,
    usuario: userMap[p.idUsuario] || null,
  }));

  const lastVisible = snapshot.docs[snapshot.docs.length - 1];

  return {
    paquetes: paquetesConUsuario,
    lastDoc: lastVisible,
  };
}

export async function updatePaquete(data: Paquete, oldData?: Paquete) {
  try {
    const result = await guardarPaquete({ paquete: data, edit: true });
    console.log({ result });
    
    // Si el envío cambió, actualizar conteos de ambos envíos (anterior y nuevo)
    const { updateEnvioCantidadPaquetes } = await import("../envios");
    
    // Actualizar el envío anterior si existía y cambió
    if (oldData?.envio && oldData.envio !== data.envio) {
      await updateEnvioCantidadPaquetes(oldData.envio);
    }
    
    // Actualizar el nuevo envío si existe
    if (data.envio) {
      await updateEnvioCantidadPaquetes(data.envio);
    }
    
    return result;
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.log({ error });
    throw error;
  }
}

export async function savePaquete(data: Paquete) {
  try {
    const { id: _id, ...paquete } = data;
    const result = await guardarPaquete({ paquete, edit: false });
    console.log({ result });
    return result;
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.log({ error });
    throw error;
  }
}

type UpdateField =
  | "estado"
  | "idUsuario"
  | "via"
  | "tarifa"
  | "cobroAdicional"
  | "envio"
  | "ubicacionFisica";

/** Paquete que no puede entregarse porque le falta peso y/o tarifa. */
export interface PaqueteEntregaIncompleta {
  id: string;
  idRastreo: string;
  faltantes: CampoEntrega[];
}

/** Se lanza al intentar entregar en lote paquetes sin peso o sin tarifa. */
export class EntregaIncompletaError extends Error {
  readonly paquetes: PaqueteEntregaIncompleta[];

  constructor(paquetes: PaqueteEntregaIncompleta[]) {
    super("Hay paquetes sin peso o tarifa que no pueden marcarse como entregados");
    this.name = "EntregaIncompletaError";
    this.paquetes = paquetes;
    Object.setPrototypeOf(this, EntregaIncompletaError.prototype);
  }
}

export const updatePackagesInBatch = async ({
  ids,
  field,
  value,
  estadosEntregado = [],
}: {
  ids: string[];
  field: UpdateField;
  value: any;
  /** Ids de estado que cuentan como entregado (`agencia.estadosEntregado`). */
  estadosEntregado?: string[];
}) => {
  const batch = writeBatch(database);
  
  // Si se actualiza el campo envio, guardar los envíos anteriores antes de actualizar
  const enviosToUpdate = new Set<string>();
  if (field === "envio") {
    for (const id of ids) {
      const ref = doc(database, "paquetes", id);
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      
      const data = snap.data() as Paquete;
      const oldEnvio = data.envio;
      const newEnvio = value as string | null;
      
      if (oldEnvio && oldEnvio !== newEnvio) {
        enviosToUpdate.add(oldEnvio);
      }
      if (newEnvio) {
        enviosToUpdate.add(newEnvio);
      }
    }
  }

  // Un paquete no puede pasar a un estado entregado sin peso ni tarifa: se
  // valida todo el lote antes de escribir para no dejarlo a medias.
  const snapsEstado = new Map<string, Paquete>();
  if (field === "estado" && estadosEntregado.includes(value as string)) {
    const incompletos: PaqueteEntregaIncompleta[] = [];

    for (const id of ids) {
      const snap = await getDoc(doc(database, "paquetes", id));
      if (!snap.exists()) continue;

      const data = { id: snap.id, ...snap.data() } as Paquete;
      snapsEstado.set(id, data);

      const faltantes = camposFaltantesParaEntrega(data);
      if (faltantes.length > 0) {
        incompletos.push({ id, idRastreo: data.idRastreo, faltantes });
      }
    }

    if (incompletos.length > 0) {
      throw new EntregaIncompletaError(incompletos);
    }
  }

  for (const id of ids) {
    const ref = doc(database, "paquetes", id);

    if (field === "estado") {
      const cached = snapsEstado.get(id);
      let data: Paquete | undefined = cached;

      if (!data) {
        const snap = await getDoc(ref);
        if (!snap.exists()) continue;
        data = { id: snap.id, ...snap.data() } as Paquete;
      }
      const rastreoActual = data.rastreo ?? [];
      const fechaNicaragua = new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/Managua" })
      );
      const fechaStr = fechaNicaragua.toISOString().split("T")[0];
      const horaStr = fechaNicaragua.toTimeString().slice(0, 5);

      const lastState = rastreoActual[rastreoActual.length - 1]?.estado;
      const changeState = value !== lastState;

      const nuevoRastreo = changeState
        ? [
          ...rastreoActual,
          {
            estado: value,
            fecha: fechaStr,
            hora: horaStr,
          },
        ]
        : rastreoActual;

      batch.update(ref, {
        estado: value,
        rastreo: nuevoRastreo,
      });
    } else if (field === "tarifa") {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      const data = snap.data() as Paquete;
      const fee = value as Fee;
      const baseTotal = fee.monto * data.peso.monto;
      const cobroAdicional = data.cobroAdicional || 0;
      const descuento = data.descuento || 0;
      const subtotal = baseTotal + cobroAdicional;
      const finalTotal = Math.round((subtotal - (descuento > 0 ? descuento : 0)) * 100) / 100;
      
      // Calculate ganancia = total - costo
      const costo = data.costo || 0;
      const ganancia = Math.round((finalTotal - costo) * 100) / 100;
      
      batch.update(ref, {
        [field]: value,
        total: finalTotal,
        ganancia: ganancia,
      });
    } else if (field === "cobroAdicional") {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      const data = snap.data() as Paquete;
      const baseTotal = data.tarifa ? data.tarifa.monto * data.peso.monto : 0;
      const cobroAdicional = value as number;
      const descuento = data.descuento || 0;
      const subtotal = baseTotal + cobroAdicional;
      const finalTotal = Math.round((subtotal - (descuento > 0 ? descuento : 0)) * 100) / 100;
      
      // Calculate ganancia = total - costo
      const costo = data.costo || 0;
      const ganancia = Math.round((finalTotal - costo) * 100) / 100;
      
      batch.update(ref, {
        [field]: value,
        total: finalTotal,
        ganancia: ganancia,
      });
    } else if (field === "envio") {
      // Si el valor es null, undefined o string vacío (después de trim), eliminar el campo
      const envioValue = typeof value === "string" ? value.trim() : value;
      if (envioValue === null || envioValue === undefined || envioValue === "") {
        batch.update(ref, { [field]: deleteField() });
      } else {
        batch.update(ref, { [field]: envioValue });
      }
    } else if (field === "ubicacionFisica") {
      // Permitir limpiar el campo dejando vacío
      const ubicacionValue = typeof value === "string" ? value.trim() : value;
      if (
        ubicacionValue === null ||
        ubicacionValue === undefined ||
        ubicacionValue === ""
      ) {
        batch.update(ref, { [field]: deleteField() });
      } else {
        batch.update(ref, { [field]: ubicacionValue });
      }
    } else {
      batch.update(ref, { [field]: value });
    }
  }

  await batch.commit();

  // Si se actualizó el campo envio, actualizar conteos de envíos afectados
  if (field === "envio" && enviosToUpdate.size > 0) {
    const { updateEnvioCantidadPaquetes } = await import("../envios");
    
    // Actualizar conteos de envíos afectados
    for (const nombreEnvio of Array.from(enviosToUpdate)) {
      try {
        await updateEnvioCantidadPaquetes(nombreEnvio);
      } catch (error) {
        if (isAuthError(error)) {
          handleAuthError();
          return;
        }
        console.error(`Error actualizando envío ${nombreEnvio}:`, error);
      }
    }
  }

  if (field === "estado") { // Sólo se envían las notificaciones si se modifica el estado
    const enviarNotificaciones = httpsCallable(
      functions,
      "enviarNotificacionesEnBatch"
    );

    try {
      await enviarNotificaciones({ paqueteIds: ids });
    } catch (error) {
      if (isAuthError(error)) {
        handleAuthError();
        return;
      }
      console.error("Error enviando notificaciones:", error);
    }
  }
};

export const deletePackage = async (id: string) => {
  try {
    await deleteDoc(doc(database, 'paquetes', id));
    console.log(`Paquete con ID ${id} eliminado exitosamente.`);
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error('Error al eliminar el paquete:', error);
    throw error;
  }
};

export async function contarPaquetesDelMes() {
  const ahora = Timestamp.now();
  const ahoraDate = ahora.toDate();
  const inicioMes = new Date(ahoraDate.getFullYear(), ahoraDate.getMonth(), 1, 0, 0, 0);
  const timestampInicioMes = Timestamp.fromDate(inicioMes);
  const paquetesRef = collection(database, "paquetes");

  const q = query(
    paquetesRef,
    where("createdAt", ">=", timestampInicioMes),
    where("createdAt", "<=", ahora)
  );

  const snapshot = await getCountFromServer(q);

  return snapshot.data().count;
}

export default async function getPackagesByRange(
  startDate: Date,
  endDate: Date,
  estadosEntregado: string[] = ["entregado"]
) {
  const paquetesRef = collection(database, "paquetes");

  const q = query(
    paquetesRef,
    where("createdAt", ">=", startDate),
    where("createdAt", "<=", endDate)
  );

  const snapshot = await getDocs(q);

  const conteo: Analytics = { packages: 0, sales: 0, cuentasPorCobrar: 0, costos: 0, ganancia: 0, aereo: 0, maritimo: 0 };

  snapshot.forEach((doc) => {
    const data = doc.data();
    const { via, total: paqueteTotal, costo, estado } = data;

    conteo.packages += 1;

    if (paqueteTotal) {
      if (estadosEntregado.includes(estado)) {
        conteo.sales += paqueteTotal;
      } else {
        conteo.cuentasPorCobrar += paqueteTotal;
      }
    }

    if (costo) {
      conteo.costos += costo;
    }

    if (via === "aereo") conteo.aereo += 1;
    else if (via === "maritimo") conteo.maritimo += 1;
  });

  // Normalizar floats a 2 decimales para evitar artefactos tipo 26868.90999999997
  conteo.sales = Math.round(conteo.sales * 100) / 100;
  conteo.cuentasPorCobrar = Math.round(conteo.cuentasPorCobrar * 100) / 100;
  conteo.costos = Math.round(conteo.costos * 100) / 100;

  // Calcular ganancia como diferencia entre ingresos y costos
  conteo.ganancia = Math.round((conteo.sales - conteo.costos) * 100) / 100;

  return conteo
}

export async function getVentasByFecha(
  fechaInicio: Date,
  fechaFin: Date,
  estadosEntregado: string[] = ["entregado"]
): Promise<{ fecha: string; total: number }[]> {
  let allPaquetes: PaqueteDto[] = [];
  let lastDoc = undefined;
  let hasMore = true;

  while (hasMore) {
    const result: { paquetes: PaqueteDto[]; lastDoc?: QueryDocumentSnapshot } =
      await fetchPaquetesLoteado(lastDoc, fechaInicio, fechaFin);

    const { paquetes, lastDoc: newLastDoc } = result;

    allPaquetes = [...allPaquetes, ...paquetes];
    lastDoc = newLastDoc;
    hasMore = !!newLastDoc;
  }

  const diffDays = dayjs(fechaFin).diff(dayjs(fechaInicio), "day");
  const format = diffDays > 31 ? "MM/YYYY" : "DD/MM/YYYY";

  const agrupado: Record<string, number> = {};

  for (const p of allPaquetes) {
    const dateKey = dayjs(p.createdAt.toDate()).format(format);
    if (p.total && estadosEntregado.includes(p.estado)) {
      agrupado[dateKey] = (agrupado[dateKey] || 0) + p.total;
    }
  }

  // Inicializar todas las fechas del rango en 0
  const fechas: string[] = [];
  let current = dayjs(fechaInicio);
  const end = dayjs(fechaFin);

  while (current.isSameOrBefore(end, 'day')) {
    const key = current.format(format);
    if (!fechas.includes(key)) fechas.push(key);

    current = format === "MM/YYYY" ? current.add(1, "month") : current.add(1, "day");
  }

  return fechas.map((fecha) => ({
    fecha,
    total: agrupado[fecha] || 0,
  }));
}